import { useEffect, useMemo, useState } from 'react';
import { condoMembersAPI, unitsAPI } from '../services/api';
import api from '../services/api';
import { ManagementRoute } from '../components/RoleRoute';
import { useAuth } from '../contexts/AuthContext';
import { isPlanLimitError } from '../services/planLimitModal';
import { BackHeader } from '../components/BackHeader';

// G1 (2026-10-01, Jaime-approved): invite UI for condo MEMBERS —
// propietario / residente / familiar / inquilino. Mirrors the empleado form
// pattern (Employees.tsx). Backend contract: POST /api/condos/:condoId/members
// (src/modules/condo/routes.js F11 Mode B) with
// { name, email, phone?, role, unitId?, generateInvite? } -> 201
// { data: { member, user, invite? } }.
// Role values per backend validRoles: 'propietario', 'resident' (label
// Residente), 'familiar', 'inquilino' (accepted alias, stored as resident).
const MEMBER_ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'propietario', label: 'Propietario' },
  { value: 'resident', label: 'Residente' },
  { value: 'familiar', label: 'Familiar' },
  { value: 'inquilino', label: 'Inquilino' },
];
const roleLabel = (value: string | null | undefined) =>
  MEMBER_ROLE_OPTIONS.find((option) => option.value === value)?.label
    || (value === 'residente' ? 'Residente' : value)
    || 'Miembro';

const unitText = (u: any) => u?.name || u?.label || u?.household_name || u?.unit_number || `Unidad ${u?.id}`;

export default function InviteMember() {
  const { user, contexts, activeContextId } = useAuth();
  const activeContext = useMemo(() => contexts.find(context => context.id === activeContextId), [contexts, activeContextId]);
  const managedRoles = ['admin', 'super_admin', 'sub_admin', 'propietario'];
  const roleContext = contexts.find(context => context.kind === 'condo' && managedRoles.includes(context.role || ''));
  const condoId = activeContext?.condoId || roleContext?.condoId || (user?.condoId && managedRoles.includes(user.role) ? user.condoId : undefined);
  const condoLabel = activeContext?.label || contexts.find(context => context.condoId === condoId)?.label || 'Condominio';
  const [items, setItems] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [saving, setSaving] = useState(false);
  // Occupation link (onboarding/frontend-enable, 2026-10-01): shareable link
  // state for the LIVE POST /api/onboarding/occupation-links endpoint.
  const [occUnitId, setOccUnitId] = useState('');
  const [occLink, setOccLink] = useState('');
  const [occMode, setOccMode] = useState<'unit' | 'open' | null>(null);
  const [occBusy, setOccBusy] = useState(false);
  const [occError, setOccError] = useState('');
  const [occCopied, setOccCopied] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'propietario', unitId: '' });

  const load = async () => {
    if (!condoId) return;
    setLoading(true);
    try {
      const [m, u] = await Promise.all([condoMembersAPI.getMembers(condoId), unitsAPI.getAll(condoId)]);
      const memberData = m.data?.data;
      const unitData = u.data?.data;
      setItems(memberData?.members || memberData?.items || (Array.isArray(memberData) ? memberData : m.data?.members || []));
      setUnits(unitData?.units || unitData?.items || (Array.isArray(unitData) ? unitData : u.data?.units || []));
      setError('');
    } catch {
      setError('No se pudieron cargar los miembros del condominio.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [condoId]);

  const create = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!condoId) return;
    setSaving(true); setError(''); setNotice(''); setInviteUrl('');
    try {
      const res = await condoMembersAPI.addMember(condoId, {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        role: form.role,
        ...(form.unitId ? { unitId: form.unitId } : {}),
        generateInvite: true,
      });
      const created = res.data?.data;
      const name = created?.user?.name || form.name;
      const unitRow = units.find((u) => String(u.id) === String(form.unitId));
      const unitPart = unitRow ? ` en la unidad ${unitText(unitRow)}` : '';
      setNotice(`${roleLabel(form.role)} ${name} creado${unitPart} y agregado al condominio.`);
      if (created?.invite?.url) setInviteUrl(String(created.invite.url));
      setForm({ name: '', email: '', phone: '', role: form.role, unitId: '' });
      await load();
    } catch (err: any) {
      if (!isPlanLimitError(err)) {
        const apiError = err?.response?.data?.error;
        setError(typeof apiError === 'string' && apiError ? apiError : 'No se pudo crear el miembro.');
      }
    } finally { setSaving(false); }
  };

  // Onboarding/frontend-enable (2026-10-01): generate an occupation link —
  // LIVE backend POST /api/onboarding/occupation-links { condoId, unitId? }.
  // unitId present → mode 'unit' (pre-bound unit); absent → mode 'open'
  // (acceptor creates the unit). Single use, 7-day expiry (backend).
  const generateOccupationLink = async () => {
    if (!condoId) return;
    setOccBusy(true); setOccError(''); setOccLink(''); setOccCopied(false); setOccMode(null);
    try {
      const res = await api.post('/onboarding/occupation-links', {
        condoId,
        ...(occUnitId ? { unitId: occUnitId } : {}),
      });
      const data = res.data?.data;
      const token = data?.token;
      if (!token) throw new Error('missing token');
      setOccMode(data.mode || (occUnitId ? 'unit' : 'open'));
      setOccLink(`${window.location.origin}/accept-invite?token=${encodeURIComponent(token)}`);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const apiError = err?.response?.data?.error;
      if (code === 'UNIT_OCCUPIED') setOccError('La unidad ya está ocupada.');
      else if (code === 'UNIT_NOT_FOUND') setOccError('La unidad no pertenece a este condominio.');
      else setOccError(typeof apiError === 'string' && apiError ? apiError : 'No se pudo generar el enlace de ocupación. Intenta de nuevo.');
    } finally { setOccBusy(false); }
  };

  if (!condoId) return <ManagementRoute><main className="p-6">Selecciona un condominio para invitar usuarios.</main></ManagementRoute>;
  return (
    <ManagementRoute>
      <main className="mx-auto max-w-6xl space-y-6 p-6">
        <BackHeader fallback="/building/dashboard" breadcrumb="Administración › Invitar usuario" />
        <header>
          <div className="mb-2 inline-flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-sm font-semibold text-teal-900"><span aria-hidden="true">⌂</span><span>Contexto activo: {condoLabel}</span></div>
          <h1 className="text-2xl font-bold">Invitar usuario</h1>
          <p className="text-gray-600">Agrega propietarios, residentes, familiares o inquilinos al condominio. La unidad es opcional (para propietario, la unidad asignada queda bajo su propiedad).</p>
        </header>
        {error && <div className="rounded-lg bg-red-50 p-3 text-red-700">{error}</div>}
        {notice && <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-900">{notice}</div>}
        {inviteUrl && <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-900"><p className="font-semibold mb-1">Invitación creada. Comparte este enlace (un solo uso, caduca en 7 días):</p><div className="flex items-center gap-2"><code className="flex-1 break-all rounded bg-white/70 p-2 text-xs">{inviteUrl}</code><button type="button" onClick={() => navigator.clipboard?.writeText(inviteUrl)} className="rounded border border-teal-300 px-2 py-1 text-xs font-semibold">Copiar</button></div></div>}
        <section className="rounded-xl border bg-[var(--v-surface-1)] p-5">
          <h2 className="mb-2 text-lg font-semibold">Enlace de ocupación</h2>
          <p className="mb-3 text-sm text-gray-600">Genera un enlace autogestionado para que un propietario reclame su unidad (o cree la suya) y se sume al condominio como propietario. Un solo uso, caduca en 7 días.</p>
          <div className="grid gap-3 md:grid-cols-[1fr_auto]">
            <select value={occUnitId} onChange={e => { setOccUnitId(e.target.value); setOccError(''); }} className="rounded border p-2" aria-label="Unidad para el enlace (opcional)">
              <option value="">Sin unidad fija (el propietario crea su unidad)</option>
              {units.map(u => <option key={u.id} value={u.id}>{unitText(u)}</option>)}
            </select>
            <button type="button" onClick={generateOccupationLink} disabled={occBusy} className="rounded bg-teal-700 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
              {occBusy ? 'Generando…' : 'Generar enlace de ocupación'}
            </button>
          </div>
          {occError && <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{occError}</div>}
          {occLink && (
            <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm text-teal-900">
              <p className="font-semibold mb-1">{occMode === 'unit' ? 'Enlace para reclamar la unidad' : 'Enlace para crear unidad y sumarse'} (comparte este enlace):</p>
              <div className="flex flex-wrap items-center gap-2">
                <code className="flex-1 min-w-0 break-all rounded bg-white/70 p-2 text-xs">{occLink}</code>
                <button type="button" onClick={() => { navigator.clipboard?.writeText(occLink); setOccCopied(true); }} className="rounded border border-teal-300 px-2 py-1 text-xs font-semibold">{occCopied ? '¡Copiado!' : 'Copiar'}</button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`¡Hola! Te comparto tu enlace de Vecindus para reclamar tu unidad${occMode === 'unit' ? '' : ' (y crearla)'} en el condominio: ${occLink}`)}`}
                  target="_blank" rel="noreferrer"
                  className="rounded border border-teal-300 px-2 py-1 text-xs font-semibold"
                >Compartir por WhatsApp</a>
              </div>
            </div>
          )}
        </section>
        <section className="rounded-xl border bg-[var(--v-surface-1)] p-5">
          <h2 className="mb-4 text-lg font-semibold">Agregar miembro</h2>
          <form onSubmit={create} className="grid gap-3 md:grid-cols-2">
            <input required placeholder="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded border p-2" />
            <input required type="email" placeholder="Correo electrónico" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded border p-2" />
            <input placeholder="Teléfono (opcional)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded border p-2" />
            <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="rounded border p-2" aria-label="Rol del miembro">
              {MEMBER_ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <select value={form.unitId} onChange={e => setForm({ ...form, unitId: e.target.value })} className="rounded border p-2" aria-label="Unidad (opcional)">
              <option value="">Sin unidad (nivel condominio)</option>
              {units.map(u => <option key={u.id} value={u.id}>{unitText(u)}</option>)}
            </select>
            <button disabled={saving} className="rounded bg-teal-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? 'Guardando…' : 'Agregar miembro'}</button>
          </form>
        </section>
        <section className="rounded-xl border bg-[var(--v-surface-1)] p-5">
          <h2 className="mb-4 text-lg font-semibold">Miembros del condominio</h2>
          {loading ? <p>Cargando miembros…</p> : items.length === 0 ? <p className="text-gray-600">Aún no hay miembros registrados.</p> : (
            <div className="space-y-3">
              {items.map((m) => (
                <div key={m.user_id || m.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
                  <div>
                    <strong>{m.full_name || m.fullName || m.name}</strong>
                    <p className="text-sm text-gray-600">{roleLabel(m.role || m.roleType)}{m.unit_label || m.unitLabel ? ` · ${m.unit_label || m.unitLabel}` : ''}{m.email ? ` · ${m.email}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </ManagementRoute>
  );
}
